/**
 * Reusable key highlights field for inventory: developer, project, configuration, units.
 * Renders a list of text inputs with add/remove; value is an array of strings.
 */
import React from 'react';
import { Box, Typography, TextField, Button, IconButton } from '@mui/material';
import { Add as AddIcon, DeleteOutline } from '@mui/icons-material';

const C = {
  green: '#2e7d32',
  text: '#111827',
  sub: '#6B7280',
  border: '#E4E7EC',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: 12.5,
    height: 32,
    borderRadius: '6px',
    '& fieldset': { borderColor: C.border },
    '&:hover fieldset': { borderColor: '#B0B8C4' },
    '&.Mui-focused fieldset': { borderColor: '#2D6A4F', borderWidth: 1.5 },
  },
  '& .MuiInputLabel-root': { fontSize: 12, top: -2 },
  '& .MuiInputLabel-shrink': { top: 0 },
};

const FieldLabel = ({ label, required }) => (
  <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: C.text, lineHeight: 1, mb: 0.75 }}>
    {label}
    {required && <span style={{ color: '#DC2626', marginLeft: 2 }}>*</span>}
  </Typography>
);

/**
 * @param {Object} props
 * @param {string[]} props.value - Array of highlight strings (e.g. from key_highlights). Use [''] for one empty row.
 * @param {function(string[]): void} props.onChange - Called when the list changes.
 * @param {string} [props.label='Key highlights (optional)']
 * @param {string} [props.placeholder='e.g. 25+ years experience']
 * @param {boolean} [props.required=false]
 * @param {object} [props.sx] - Optional MUI sx for the root Box.
 */
const InventoryKeyHighlighter = ({
  value = [''],
  onChange,
  label = 'Key highlights',
  placeholder = 'e.g. 25+ years experience',
  required = false,
  subtitle = '',
  accent = 'info',
  sx = {},
}) => {
  const list = Array.isArray(value) && value.length > 0 ? value : [''];

  const updateAt = (index, text) => {
    const next = list.map((item, i) => (i === index ? text : item));
    onChange(next);
  };

  const removeAt = (index) => {
    if (list.length <= 1) return;
    const next = list.filter((_, i) => i !== index);
    onChange(next);
  };

  const add = () => {
    onChange([...list, '']);
  };

  return (
    <Box sx={{ mb: 0, ...sx }}>
      {list.map((item, index) => (
        <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <TextField
            placeholder={placeholder}
            value={item}
            onChange={(e) => updateAt(index, e.target.value)}
            fullWidth
            variant="outlined"
            size="small"
            sx={fieldSx}
          />
          <IconButton
            size="small"
            onClick={() => removeAt(index)}
            disabled={list.length <= 1}
            sx={{ color: C.sub }}
            aria-label="Remove highlight"
          >
            <DeleteOutline sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      ))}
      <Button
        startIcon={<AddIcon sx={{ fontSize: 16 }} />}
        onClick={add}
        size="small"
        sx={{ textTransform: 'none', fontSize: 12, color: C.green }}
      >
        Add highlight
      </Button>
    </Box>
  );
};

export default InventoryKeyHighlighter;
